/**
 * The agent loop behind the in-page "bring your own key" panel (SPEC §7).
 *
 * `runAgent()` sends the conversation to a `Provider`, executes every tool
 * call the model asks for, feeds the results back, and repeats until the model
 * answers with plain text or `maxRounds` is hit. It drives the SAME WebMCP
 * tools the browser exposes:
 *
 * - **Native path** (ChatGPT desktop / Chrome 149+ with the flag): tools come
 *   from `document.modelContext.getTools()` (each `inputSchema` is a JSON
 *   *string*, parsed here) and run through
 *   `document.modelContext.executeTool(registeredTool, JSON.stringify(args))`,
 *   which returns a string that is passed to the model untouched.
 * - **Fallback path**: the local registry (`webmcp/register.ts`), invoked as
 *   `registry.invoke(name, args, 'agent')` with the result JSON-stringified.
 *
 * The tool list is resolved again before every model call, so a `toolchange`
 * between rounds (writers disposed during an audition, then re-registered)
 * is never executed through a stale handle. A `signal` abort wins the race
 * against a tool that is still running (an audition waiting for the human),
 * and any tool calls left unanswered by a failure are closed with an error
 * result so the returned history is always valid for a follow-up turn.
 *
 * Nothing here throws to the caller: provider failures, malformed arguments,
 * unknown tools, executor exceptions and aborts are reported through
 * `onEvent({type:'error'})` / tool results and a `RunAgentResult` with `ok:false`.
 */
import { registry } from '../webmcp/register';
import { ProviderError, decodeArguments, type ChatMessage, type Provider, type ToolCall, type ToolSpec } from './providers';

/** System prompt for the session musician; kept under 900 characters. */
export const SYSTEM_PROMPT = [
  "You are Bandmate's session musician, arranging inside a 4-bar loop (drums, bass, chords, lead) that keeps playing while you work.",
  'You cannot hear the audio, so read before you write: call get_session first, then get_track for detail.',
  'Express musical intent through the tools: suggest_chords then add_chords for harmony, add_bass for a bass line, add_drums for a beat; use write_notes or set_drum_steps only for precise edits.',
  'When the human should choose by ear, call audition with two or three options and let them pick; do not decide for them.',
  'Human-written notes belong to the human; the tools will ask before removing them.',
  'Reply in one or two sentences, plainly: what changed and what to try next.',
  'Never claim to have heard anything; describe what the grid now contains instead.',
].join(' ');

/** Maximum messages kept in the follow-up history (system prompt excluded). */
export const HISTORY_CAP = 40;

/** Default cap on provider round-trips per user turn. */
export const DEFAULT_MAX_ROUNDS = 12;

/** A tool the loop can offer the model, plus the native handle needed to run it. */
export interface ResolvedTool {
  spec: ToolSpec;
  /** The `RegisteredTool` object from `document.modelContext.getTools()`; absent on the local path. */
  handle?: unknown;
}

/** Resolves the current tool list (called before every model call). */
export type ToolsSource = () => Promise<ResolvedTool[]>;

/** Executes one tool call and returns the result as text for the model. */
export type ToolExecutor = (tool: ResolvedTool, args: Record<string, unknown>, signal?: AbortSignal) => Promise<string>;

/** Progress events emitted while a turn runs; the panel renders these. */
export type AgentEvent =
  | { type: 'thinking'; round: number }
  | { type: 'tool_call'; id: string; name: string; arguments: Record<string, unknown>; round: number }
  | { type: 'tool_result'; id: string; name: string; ok: boolean; result: string; durationMs: number }
  | { type: 'assistant'; text: string }
  | { type: 'error'; message: string; aborted?: boolean };

export interface RunAgentOptions {
  provider: Provider;
  apiKey: string;
  model: string;
  /** The human's new message. */
  userText: string;
  /** Prior turns (no system message); returned extended in the result. */
  history: ChatMessage[];
  /** Defaults to `resolveTools` (native modelContext, else local registry); called before every round. */
  toolsSource?: ToolsSource;
  /** Defaults to `executeResolvedTool`. */
  executor?: ToolExecutor;
  onEvent?: (e: AgentEvent) => void;
  /** Max provider calls for this turn (default 12). */
  maxRounds?: number;
  signal?: AbortSignal;
  /** Override the system prompt (tests / experiments). */
  systemPrompt?: string;
  /** Passed to the provider (tests). */
  fetchImpl?: typeof fetch;
}

export interface RunAgentResult {
  /** True when the model finished with a text reply. */
  ok: boolean;
  /** The final assistant text ('' when none). */
  text: string;
  /** History to pass to the next turn (capped at `HISTORY_CAP`, no system message). */
  history: ChatMessage[];
  /** Provider calls made. */
  rounds: number;
  /** Readable failure message when `ok` is false. */
  error?: string;
  /** True when the run was stopped through `signal`. */
  aborted?: boolean;
}

// ---------- tool sources & executors ----------

interface ModelContextSurface {
  getTools?: () => unknown;
  executeTool?: (tool: unknown, input: string) => unknown;
}

function modelContext(): ModelContextSurface | null {
  const mc = (globalThis as { document?: { modelContext?: unknown } }).document?.modelContext;
  return mc && typeof mc === 'object' ? (mc as ModelContextSurface) : null;
}

function parseSchema(v: unknown): Record<string, unknown> {
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
    } catch {
      /* fall through */
    }
    return { type: 'object', properties: {} };
  }
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return { type: 'object', properties: {} };
}

/**
 * Default `ToolsSource`: `document.modelContext.getTools()` when present
 * (schemas parsed from their JSON strings, native handles kept), else the
 * local registry.
 */
export const resolveTools: ToolsSource = async () => {
  const mc = modelContext();
  if (mc && typeof mc.getTools === 'function') {
    try {
      const raw = await mc.getTools();
      if (Array.isArray(raw)) {
        const out: ResolvedTool[] = [];
        for (const t of raw as unknown[]) {
          const r = t as { name?: unknown; description?: unknown; inputSchema?: unknown } | null;
          if (!r || typeof r.name !== 'string') continue;
          out.push({
            spec: { name: r.name, description: typeof r.description === 'string' ? r.description : '', inputSchema: parseSchema(r.inputSchema) },
            handle: t,
          });
        }
        return out;
      }
    } catch (e) {
      console.warn('[agent] modelContext.getTools() failed; using the local registry', e);
    }
  }
  return registry.list().map((t) => ({
    spec: { name: t.def.name, description: t.def.description, inputSchema: t.def.inputSchema as Record<string, unknown> },
  }));
};

function errorResult(code: string, message: string, extra: Record<string, unknown> = {}): string {
  return JSON.stringify({ error: { code, message, ...extra } });
}

/**
 * Default `ToolExecutor`: native `executeTool(handle, JSON.stringify(args))`
 * when the tool carries a handle and the surface exists, else
 * `registry.invoke(name, args, 'agent')` stringified.
 */
export const executeResolvedTool: ToolExecutor = async (tool, args, signal) => {
  const mc = modelContext();
  if (tool.handle !== undefined && mc && typeof mc.executeTool === 'function') {
    const raw = await mc.executeTool(tool.handle, JSON.stringify(args));
    if (typeof raw === 'string') return raw;
    return raw === undefined ? '' : JSON.stringify(raw);
  }
  const wrapped = registry.get(tool.spec.name);
  if (!wrapped) return errorResult('unknown_tool', `no tool named "${tool.spec.name}"`);
  const result = await wrapped.invoke(args, 'agent', signal);
  return typeof result === 'string' ? result : JSON.stringify(result ?? null);
};

// ---------- history ----------

/**
 * Keep the newest `cap` messages, never starting on an assistant turn or a
 * tool result (the APIs reject orphaned tool results).
 */
export function trimHistory(history: ChatMessage[], cap = HISTORY_CAP): ChatMessage[] {
  let start = Math.max(0, history.length - cap);
  while (start < history.length) {
    const m = history[start]!;
    if (m.role === 'user' && !m.toolCallId) break;
    start++;
  }
  return history.slice(start);
}

// ---------- the loop ----------

function abortedError(): ProviderError {
  return new ProviderError('Stopped.', 'aborted');
}

/**
 * Resolve with `promise`, or reject with an abort error as soon as `signal`
 * fires — whichever comes first. A tool that keeps running after the human
 * pressed Stop (an audition waiting for a pick) must not keep the panel busy;
 * its late settlement is swallowed so nothing rejects unhandled.
 */
function withAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) {
    promise.catch(() => undefined);
    return Promise.reject(abortedError());
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortedError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (v) => {
        signal.removeEventListener('abort', onAbort);
        resolve(v);
      },
      (e: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(e);
      },
    );
  });
}

/**
 * Append error results for every tool call of the last assistant turn that
 * has no matching `tool` message. Both APIs reject a transcript with an
 * unanswered tool call, so a turn cut short by Stop or a failure must be
 * closed before the history is reused.
 */
export function closeOpenToolCalls(history: ChatMessage[], message = 'stopped before this tool ran'): ChatMessage[] {
  let lastAssistant = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]!.role === 'assistant') {
      lastAssistant = i;
      break;
    }
  }
  if (lastAssistant < 0) return history;
  const calls = history[lastAssistant]!.toolCalls ?? [];
  if (!calls.length) return history;
  const answered = new Set(history.slice(lastAssistant + 1).filter((m) => m.role === 'tool').map((m) => m.toolCallId));
  const out = [...history];
  for (const call of calls) {
    if (answered.has(call.id)) continue;
    out.push({ role: 'tool', content: errorResult('aborted', message), toolCallId: call.id, toolName: call.name });
  }
  return out;
}

function messageOf(e: unknown): string {
  if (e instanceof ProviderError) return e.message;
  if (e instanceof Error) return e.message || e.name;
  return String(e);
}

/**
 * Run one user turn: call the model, execute tool calls, repeat until it
 * answers in text or `maxRounds` is exhausted. Never rejects.
 */
export async function runAgent(opts: RunAgentOptions): Promise<RunAgentResult> {
  const {
    provider,
    apiKey,
    model,
    userText,
    toolsSource = resolveTools,
    executor = executeResolvedTool,
    onEvent,
    maxRounds = DEFAULT_MAX_ROUNDS,
    signal,
    systemPrompt = SYSTEM_PROMPT,
    fetchImpl,
  } = opts;
  const emit = (e: AgentEvent) => {
    try {
      onEvent?.(e);
    } catch (err) {
      console.error('[agent] onEvent handler threw', err);
    }
  };

  const history: ChatMessage[] = trimHistory([...opts.history, { role: 'user', content: userText }]);
  let rounds = 0;
  let lastText = '';

  const fail = (error: string, aborted = false): RunAgentResult => {
    emit(aborted ? { type: 'error', message: error, aborted: true } : { type: 'error', message: error });
    const closed = closeOpenToolCalls(history, aborted ? 'stopped by the human before this tool ran' : 'the turn ended before this tool ran');
    return { ok: false, text: lastText, history: trimHistory(closed), rounds, error, aborted };
  };
  const checkAbort = () => {
    if (signal?.aborted) throw abortedError();
  };

  try {
    checkAbort();
    while (rounds < maxRounds) {
      checkAbort();
      rounds++;
      emit({ type: 'thinking', round: rounds });
      // Re-resolve every round: a `toolchange` between rounds (writers disposed
      // during an audition, then re-registered) invalidates native handles.
      let tools: ResolvedTool[] = [];
      try {
        tools = await withAbort(toolsSource(), signal);
      } catch (e) {
        if (signal?.aborted) throw abortedError();
        console.warn('[agent] could not resolve tools; continuing with none', e);
      }
      const byName = new Map(tools.map((t) => [t.spec.name, t]));
      const specs = tools.map((t) => t.spec);
      const completion = await provider.complete([{ role: 'system', content: systemPrompt }, ...history], specs, {
        apiKey,
        model,
        signal,
        fetchImpl,
      });
      checkAbort();

      const text = completion.text ?? '';
      const calls: ToolCall[] = completion.toolCalls ?? [];
      if (!calls.length) {
        lastText = text;
        history.push({ role: 'assistant', content: text });
        emit({ type: 'assistant', text });
        return { ok: true, text, history: trimHistory(history), rounds };
      }

      if (text) lastText = text;
      history.push({ role: 'assistant', content: text, toolCalls: calls });
      if (text) emit({ type: 'assistant', text });

      for (const call of calls) {
        checkAbort();
        emit({ type: 'tool_call', id: call.id, name: call.name, arguments: call.arguments, round: rounds });
        const started = performance.now();
        let result: string;
        let ok = true;
        if (call.argumentsError) {
          ok = false;
          const message = `${call.name}: ${call.argumentsError}`;
          emit({ type: 'error', message: `The model sent malformed arguments for ${call.name}; asked it to retry.` });
          result = errorResult('invalid_arguments', message, {
            hint: 'send the arguments as a JSON object matching the tool schema',
            ...(call.rawArguments !== undefined ? { received: call.rawArguments.slice(0, 200) } : {}),
          });
        } else {
          const tool = byName.get(call.name);
          if (!tool) {
            ok = false;
            result = errorResult('unknown_tool', `no tool named "${call.name}"`, { available: [...byName.keys()] });
          } else {
            try {
              result = await withAbort(executor(tool, call.arguments, signal), signal);
              ok = !looksLikeError(result);
            } catch (e) {
              if (signal?.aborted) throw abortedError();
              ok = false;
              result = errorResult('execution_failed', messageOf(e));
            }
          }
        }
        history.push({ role: 'tool', content: result, toolCallId: call.id, toolName: call.name });
        emit({ type: 'tool_result', id: call.id, name: call.name, ok, result, durationMs: Math.round(performance.now() - started) });
      }
    }
    return fail(`Stopped after ${maxRounds} rounds of tool calls without a final answer. Ask again or narrow the request.`);
  } catch (e) {
    const aborted = signal?.aborted === true || (e instanceof ProviderError && e.kind === 'aborted') || (e as { name?: string })?.name === 'AbortError';
    return fail(aborted ? 'Stopped.' : messageOf(e), aborted);
  }
}

/** True when a tool result text is a `{error:{…}}` envelope. */
export function looksLikeError(resultText: string): boolean {
  const t = resultText.trimStart();
  if (!t.startsWith('{')) return false;
  const { arguments: parsed, argumentsError } = decodeArguments(t);
  return !argumentsError && 'error' in parsed && parsed.error != null;
}
